import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import EmptyState from '@/components/ui/EmptyState'
import MenuSingle from '@/components/ui/MenuSingle'
import Pagination from '@/components/ui/Pagination'
import RefreshBar from '@/components/ui/RefreshBar'
import SearchInput from '@/components/ui/SearchInput'
import SelectTrigger from '@/components/ui/SelectTrigger'
import Skeleton from '@/components/ui/Skeleton'
import TabBar, { type TabBarOption } from '@/components/ui/TabBar'
import TitleRow from '@/components/ui/TitleRow'
import { useDepthSectors } from '@/features/depth/api/queries'
import { useLastClose } from '@/features/market/lib/useLastClose'
import { pricesKeys } from '@/features/prices/api/keys'
import { useWeek52, useWeek52Nepse } from '@/features/prices/api/queries'
import Week52Table from '@/features/prices/components/Week52Table'
import { useManualRefresh } from '@/hooks/useManualRefresh'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import { loadWeek52, toWeek52Query, week52SearchSchema } from '@/screens/market/Week52Screen.loader'
import type { Week52Page, Week52Sort } from '@/schemas/prices'
import type { PageSize } from '@/schemas/table'

const SORT_TABS: readonly TabBarOption[] = [
  { value: 'nearest-high', label: '52-Week High' },
  { value: 'nearest-low', label: '52-Week Low' },
]

const ALL_SECTORS = { value: '', label: 'All sectors' }

const HEADER = <TitleRow title="52W Change" />

/**
 * The loaded page, block for block: the high/low tabs (36), the search and
 * sector row (44 below `lg`, 36 from it), then the two-line table (`h-table-head`
 * band, ten `h-table-row-two-line` rows) with `Pagination`'s own `mt-6` and
 * 44 / 32 foot.
 */
function Week52Pending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading 52-week change…"
      header={HEADER}
      pageTitle="52W Change"
    >
      <div className="flex flex-col gap-4">
        <div className="flex h-9 items-center gap-5">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Skeleton className="h-11 w-70 max-w-full lg:h-9" />
          <Skeleton className="h-11 w-40 max-w-full lg:h-9" />
        </div>
      </div>
      <div>
        <DataTableSkeleton rows={10} columns={5} rowHeight="lg" />
        <div className="mt-6 h-11 lg:h-8" />
      </div>
    </ScreenLayout>
  )
}

function Week52Error(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Couldn't load 52-week change"
      description="The data did not come back. Nothing on your device is wrong."
    />
  )
}

export const Route = createFileRoute('/_app/market/52-week')({
  validateSearch: week52SearchSchema,
  loader: loadWeek52,
  pendingComponent: Week52Pending,
  errorComponent: Week52Error,
  component: Week52Screen,
})

/**
 * Market · 52-week change. Order, top to bottom: the high / low tabs, the
 * search and the sector menu on one row, the table in its card, pagination.
 */
function Week52Screen() {
  // Re-parsed through the loader's own schema rather than read straight off
  // `Route.useSearch()`: the router merges the URL's raw query string into
  // that object, so an unrecognised param (`?utm_source=...`) would otherwise
  // key `useWeek52` here off a different object than the one the loader
  // warmed for the entry URL — see `Week52Screen.loader.ts`'s own comment on
  // `toWeek52Query`.
  const search = toWeek52Query(Route.useSearch())
  const navigate = Route.useNavigate()
  const online = useOnline()
  const query = useWeek52(search)
  const manual = useManualRefresh([query])
  const sectors = useDepthSectors()
  const nepse = useWeek52Nepse()
  const [sectorOpen, setSectorOpen] = useState(false)

  // The same 300ms debounce Stock prices uses for its own search: the term
  // lands in the URL as `query`, guarded against the mount-time "change" so a
  // page nobody typed into is not sent a stray `page: 1`.
  const [term, setTerm] = useState(search.query ?? '')
  const lastCommittedQuery = useRef(search.query)
  useEffect(() => {
    const next = term.trim() || undefined
    if (next === lastCommittedQuery.current) return
    const id = setTimeout(() => {
      lastCommittedQuery.current = next
      void navigate({ search: (prev) => ({ ...prev, query: next, page: 1 }) })
    }, 300)
    return () => {
      clearTimeout(id)
    }
  }, [term, navigate])

  const lastClose = useLastClose<Week52Page>(pricesKeys.week52Tables(), query.data)
  const data = lastClose.data
  const view = lastClose.isShowingSaved ? 'loaded' : resolveViewState(query, { online })
  const sectorOptions = [
    ALL_SECTORS,
    ...(sectors.data ?? []).map((s) => ({ value: s.id, label: s.name })),
  ]
  // The index's own line, pinned first on page 1 of both tabs whatever the sort (the mock). A sector
  // has no index row here, and a search keeps it only while it matches the index's name.
  const needle = search.query?.trim().toLowerCase() ?? ''
  const pinned =
    nepse.data !== null &&
    nepse.data !== undefined &&
    search.page === 1 &&
    search.sector === undefined &&
    (needle === '' || 'nepse index'.includes(needle))
      ? [nepse.data]
      : []
  const rows = [...pinned, ...(data?.rows ?? [])]
  const sector = search.sector ?? ''
  const sectorLabel = sectorOptions.find((s) => s.value === sector)?.label ?? ALL_SECTORS.label

  return (
    <ScreenLayout header={HEADER} pageTitle="52W Change">
      {lastClose.isShowingSaved ? <RefreshBar state="offline" onRetry={manual.refresh} /> : null}

      <div className="flex flex-col gap-4">
        {/* Which 52-week bound the table orders against: an underline TabBar, as
            Movers uses for its views. */}
        <TabBar
          options={SORT_TABS}
          value={search.sort}
          ariaLabel="52-week ordering"
          onChange={(value) => {
            void navigate({
              search: (prev) => ({
                ...prev,
                sort: value as Week52Sort,
                sortBy: undefined,
                sortDesc: undefined,
                page: 1,
              }),
            })
          }}
        />

        {/* Search on the left, the sector menu on the right — the one sector
            menu that can carry a long label ("Manufacturing and processing" runs
            226px), so `max-w-full` stays on its trigger. */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <SearchInput
            aria-label="Search a company or a symbol"
            placeholder="Search a company or a symbol"
            value={term}
            onChange={(event) => {
              setTerm(event.target.value)
            }}
            className="w-full @sm:w-70"
          />
          {/* An empty string is the absence of a filter, not a value: the menu
              keeps it so the list has a row to point at, and `|| undefined`
              drops `sector` out of the URL again on the way back. */}
          <MenuSingle
            options={sectorOptions}
            value={sector}
            onChange={(value) => {
              void navigate({
                search: (prev) => ({ ...prev, sector: value || undefined, page: 1 }),
              })
            }}
            open={sectorOpen}
            onOpenChange={setSectorOpen}
            label="Sector"
            searchable={false}
            align="end"
            trigger={(triggerProps) => (
              <SelectTrigger
                {...triggerProps}
                className="max-w-full"
                aria-label={`Filter by sector, ${sectorLabel}`}
                label={sectorLabel}
                open={sectorOpen}
                filled={search.sector !== undefined}
              />
            )}
          />
        </div>
      </div>

      {view === 'failed' || view === 'offline' ? (
        <EmptyState
          kind={view}
          size="page"
          title={view === 'offline' ? undefined : "Couldn't load 52-week change"}
          onRetry={manual.refresh}
          retrying={manual.isManual}
          meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
          savedAt={lastClose.savedAt}
          onShowLastClose={lastClose.showLastClose}
        />
      ) : (
        <>
          {/* Table and pager travel as ONE block of the stack: `Pagination` draws
              its own 24px (see its PLACEMENT note) and a parent `gap-*` would add
              to it rather than absorb it. */}
          <div>
            <Week52Table
              basis={search.sort}
              rows={rows}
              isLoading={query.isLoading}
              pageSize={search.pageSize}
              query={search.query}
              sortBy={search.sortBy}
              sortDesc={search.sortDesc}
              onSortChange={(sortBy, sortDesc) => {
                void navigate({
                  search: (prev) => ({
                    ...prev,
                    sortBy,
                    sortDesc: sortBy === undefined ? undefined : sortDesc,
                    page: 1,
                  }),
                })
              }}
            />

            <Pagination
              page={data?.page ?? search.page}
              pageSize={data?.pageSize ?? search.pageSize}
              total={data?.total ?? 0}
              // `/market/fifty-two-week/` merges no page meta, but its ceiling now
              // sits above the number of companies it ranks, so `total` is every
              // one of them and the plain unit is true (Live market still has to
              // say which 200 it holds).
              unit="companies"
              onPageChange={(page) => {
                void navigate({ search: (prev) => ({ ...prev, page }) })
              }}
              onPageSizeChange={(pageSize) => {
                void navigate({
                  search: (prev) => ({ ...prev, pageSize: pageSize as PageSize, page: 1 }),
                })
              }}
            />

            {/* The mock's one line under the pager: what the order means, and what it does not. */}
            {data === undefined ? null : (
              <p className="mt-4 text-body-xs text-muted-foreground-subtle">{data.footnote}</p>
            )}
          </div>
        </>
      )}
    </ScreenLayout>
  )
}
